import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { SilentRequest, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { BehaviorSubject, Observable, timer, of } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { UserAuthService } from './user-auth.service';
import { AuthCookieManagerService } from './auth-cookie-manager/auth-cookie-manager.service';
import { protectedResources } from './auth-config';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  private refreshTimer$ = new BehaviorSubject<boolean>(false);
  private readonly TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
  private readonly TOKEN_REFRESH_THRESHOLD = 10 * 60 * 1000; // Refresh 10 minutes before expiry
  private isRefreshing = false;

  constructor(
    private msalService: MsalService,
    private userAuthService: UserAuthService,
    private authCookieManager: AuthCookieManagerService
  ) {
    this.startTokenRefreshTimer();
  }

  /**
   * Start automatic token refresh timer
   */
  private startTokenRefreshTimer(): void {
    console.log('🔄 Token Refresh: Starting automatic token monitoring...');
    timer(0, this.TOKEN_REFRESH_INTERVAL)
      .pipe(
        switchMap(() => this.checkAndRefreshToken()),
        catchError(error => {
          console.error('❌ Token Refresh: Timer error:', error);
          return of(false);
        })
      )
      .subscribe();
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private checkAndRefreshToken(): Observable<boolean> {
    // Prevent multiple refresh attempts
    if (this.isRefreshing) {
      return of(true);
    }

    const user = this.userAuthService.getUserInfoFromLocal();
    
    if (!user || !user.msal_expiry) {
      console.log('🔍 Token Check: No user or token expiry found');
      return of(false);
    }

    const expiryTime = moment(user.msal_expiry);
    const currentTime = moment();
    const timeUntilExpiry = expiryTime.diff(currentTime);
    const minutesUntilExpiry = Math.round(timeUntilExpiry / (1000 * 60));

    // Log the periodic check
    console.log(`🔍 Token Check: Token expires in ${minutesUntilExpiry} minutes (${expiryTime.format('HH:mm:ss')})`);

    // If token expires within threshold, refresh it
    if (timeUntilExpiry <= this.TOKEN_REFRESH_THRESHOLD && timeUntilExpiry > 0) {
      console.log(`⚠️  Token Refresh: Token expires in ${minutesUntilExpiry} minutes - Starting silent refresh...`);
      return this.silentRefreshToken();
    }

    // If token is already expired, attempt refresh
    if (timeUntilExpiry <= 0) {
      console.log('❌ Token Refresh: Token has expired - Attempting silent refresh...');
      return this.silentRefreshToken();
    }

    return of(true);
  }

  /**
   * Perform silent token refresh
   */
  public silentRefreshToken(): Observable<boolean> {
    if (this.isRefreshing) {
      return of(true);
    }

    this.isRefreshing = true;
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      console.error('❌ Token Refresh: No accounts found for refresh');
      this.isRefreshing = false;
      return of(false);
    }

    const silentRequest: SilentRequest = {
      scopes: protectedResources.tokenService.scopes,
      account: accounts[0],
      forceRefresh: false
    };

    return new Observable<boolean>(observer => {
      this.msalService.instance.acquireTokenSilent(silentRequest)
        .then((response: AuthenticationResult) => {
          console.log('✅ Token Refresh: Silent refresh successful');
          this.updateStoredTokenInfo(response);
          this.isRefreshing = false;
          observer.next(true);
          observer.complete();
        })
        .catch(error => {
          console.warn('⚠️  Token Refresh: Silent refresh failed, trying interactive...', error.name);
          this.isRefreshing = false;
          
          // If silent refresh fails due to interaction required, try interactive refresh
          if (error instanceof InteractionRequiredAuthError) {
            this.interactiveRefreshToken().subscribe(
              success => {
                observer.next(success);
                observer.complete();
              },
              err => {
                observer.error(err);
              }
            );
          } else {
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  /**
   * Perform interactive token refresh (popup)
   */
  private interactiveRefreshToken(): Observable<boolean> {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      console.error('❌ Token Refresh: No accounts found for interactive refresh');
      return of(false);
    }

    return new Observable<boolean>(observer => {
      this.msalService.instance.acquireTokenPopup({
        scopes: protectedResources.tokenService.scopes,
        account: accounts[0]
      })
      .then((response: AuthenticationResult) => {
        console.log('✅ Token Refresh: Interactive refresh successful');
        this.updateStoredTokenInfo(response);
        observer.next(true);
        observer.complete();
      })
      .catch(error => {
        console.error('❌ Token Refresh: Interactive refresh failed - User needs to re-login');
        // If interactive refresh also fails, user needs to login again
        this.userAuthService.logOut();
        observer.next(false);
        observer.complete();
      });
    });
  }

  /**
   * Update stored token information
   */
  private updateStoredTokenInfo(authResult: AuthenticationResult): void {
    const user = this.userAuthService.getUserInfoFromLocal();
    if (user) {
      // Debug logging for token expiry investigation
      console.log('🔧 DEBUG: MSAL authResult.expiresOn:', authResult.expiresOn);
      console.log('🔧 DEBUG: Current time:', moment().format('YYYY-MM-DD HH:mm:ss Z'));
      
      // Calculate new expiry time with proper timezone handling
      const bufferMinutes = 5; // 5-minute buffer
      let rawExpiryTime: moment.Moment;
      
      if (authResult.expiresOn) {
        // Force moment to parse as the actual date object (preserves timezone)
        rawExpiryTime = moment(new Date(authResult.expiresOn));
        console.log('🔧 DEBUG: Using MSAL expiry date');
      } else {
        rawExpiryTime = moment().add(60, 'minutes');
        console.log('🔧 DEBUG: Using fallback 60 minutes');
      }
      
      const expiryTime = rawExpiryTime.subtract(bufferMinutes, 'minutes');
      
      console.log('🔧 DEBUG: Raw expiry time (from MSAL):', rawExpiryTime.format('YYYY-MM-DD HH:mm:ss Z'));
      console.log('🔧 DEBUG: Final expiry time (with buffer):', expiryTime.format('YYYY-MM-DD HH:mm:ss Z'));
      console.log('🔧 DEBUG: Minutes from now:', Math.round(expiryTime.diff(moment(), 'minutes')));

      // Update token info
      user.msal_accessToken = authResult.accessToken;
      user.msal_expiry = expiryTime.toISOString();
      
      // Store updated user info
      this.userAuthService.localSettingsSrv.storeUserInfo('userLists', user);
      
      // Update cookie if using OIDC
      if (authResult.accessToken) {
        this.authCookieManager.updateToken(
          'access_token', 
          authResult.accessToken, 
          Math.floor(expiryTime.diff(moment(), 'seconds'))
        );
      }
      
      const newExpiryMinutes = Math.round(expiryTime.diff(moment(), 'minutes'));
      console.log(`✅ Token Refresh: New token valid for ${newExpiryMinutes} minutes`);
    }
  }

  /**
   * Force token refresh
   */
  public forceRefreshToken(): Observable<boolean> {
    console.log('🔄 Token Refresh: Force refresh requested...');
    return this.silentRefreshToken();
  }

  /**
   * Validate that the persisted MSAL token cache has not been tampered with.
   *
   * The app makes its API calls with its own stored token (in `userLists`), and
   * MSAL will silently self-heal a broken token cache from the still-valid AAD
   * session cookie (it acquires a fresh token and appends a new key while
   * leaving the tampered entries behind). Because of that, `acquireTokenSilent`
   * cannot be used to detect tampering — it just succeeds.
   *
   * Instead we verify the cache is internally consistent: every credential key
   * listed in `msal.token.keys.<clientId>` must resolve to a well-formed entry
   * that belongs to this client. A dangling / altered reference (left behind by
   * editing `msal.token.keys.*` or a token entry) means the cache was tampered
   * with, and the caller should log the user out.
   *
   * Any unexpected failure of the probe itself resolves as "intact" so a user is
   * never signed out because of a problem in this check.
   */
  public validateMsalCacheIntegrity(): Observable<boolean> {
    try {
      return of(this.isMsalTokenCacheConsistent());
    } catch (error) {
      console.warn('MSAL integrity probe error - keeping session:', error);
      return of(true);
    }
  }

  private isMsalTokenCacheConsistent(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      // No account present at all — the account cache is gone/corrupted.
      return false;
    }

    const config = this.msalService.instance.getConfiguration();
    const clientId = config.auth.clientId;
    const storage: Storage =
      config.cache?.cacheLocation === 'sessionStorage' ? sessionStorage : localStorage;

    const tokenKeysRaw = storage.getItem(`msal.token.keys.${clientId}`);
    if (!tokenKeysRaw) {
      // We believe the user is signed in, but there are no token cache keys.
      return false;
    }

    let tokenKeys: { idToken?: string[]; accessToken?: string[]; refreshToken?: string[] };
    try {
      tokenKeys = JSON.parse(tokenKeysRaw);
    } catch {
      // The token keys index itself is not valid JSON → tampered.
      return false;
    }

    const referencedKeys = [
      ...(tokenKeys.idToken || []),
      ...(tokenKeys.accessToken || []),
      ...(tokenKeys.refreshToken || [])
    ];

    // A session must have at least one usable credential.
    if (referencedKeys.length === 0) {
      return false;
    }

    // Every referenced key must point to a well-formed credential for this client.
    return referencedKeys.every(key => this.isValidCredentialEntry(storage, key, clientId));
  }

  private isValidCredentialEntry(storage: Storage, key: string, clientId: string): boolean {
    const raw = storage.getItem(key);
    if (!raw) {
      // Dangling reference — the key was altered / points to a missing entry.
      return false;
    }
    try {
      const entry = JSON.parse(raw);
      if (!entry || !entry.credentialType || !entry.secret) {
        return false;
      }
      // The entry must belong to this client (guards against swapped references).
      if (entry.clientId && entry.clientId !== clientId) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if user session is still valid
   */
  public isSessionValid(): boolean {
    const user = this.userAuthService.getUserInfoFromLocal();
    if (!user || !user.msal_expiry) {
      return false;
    }

    return moment().isBefore(moment(user.msal_expiry));
  }

  /**
   * Get time until token expires (in minutes)
   */
  public getTimeUntilExpiry(): number {
    const user = this.userAuthService.getUserInfoFromLocal();
    if (!user || !user.msal_expiry) {
      return 0;
    }

    const expiryTime = moment(user.msal_expiry);
    const currentTime = moment();
    return Math.max(0, Math.floor(expiryTime.diff(currentTime, 'minutes')));
  }

  /**
   * Stop the refresh timer (for cleanup)
   */
  public stopRefreshTimer(): void {
    this.refreshTimer$.complete();
  }
}
